import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import type { LaneData } from "@/hooks/useLaneData";

type Equipment = "Van" | "Reefer" | "Flatbed";
type HaulType = "short" | "medium" | "long" | null;

const EQUIPMENT_DB_MAP: Record<Equipment, string> = {
  Van: "van",
  Reefer: "reefer",
  Flatbed: "flatbed",
};

interface HeatMapModuleProps {
  equipment: Equipment;
  onEquipmentChange: (eq: Equipment) => void;
  haulType: HaulType;
  onHaulTypeChange: (ht: HaulType) => void;
  lanes: LaneData[];
  globalAverage: number;
  isLoading: boolean;
}

export function HeatMapModule({
  equipment,
  onEquipmentChange,
  haulType,
  onHaulTypeChange,
  lanes,
  globalAverage,
  isLoading,
}: HeatMapModuleProps) {
  const navigate = useNavigate();

  const handleLaneClick = (lane: LaneData) => {
    const params = new URLSearchParams();
    if (lane.originCity && lane.originState) {
      params.set('origin', `${lane.originCity}, ${lane.originState}`);
    }
    if (lane.destCity && lane.destState) {
      params.set('destination', `${lane.destCity}, ${lane.destState}`);
    }
    params.set('equipment', EQUIPMENT_DB_MAP[equipment]);
    params.set('source', 'market-index');
    navigate(`/search?${params.toString()}`);
  };

  const haulTypes: { key: HaulType; label: string; activeColor: string }[] = [
    { key: "short", label: "Short", activeColor: "hsl(var(--brand-teal) / 0.4)" },
    { key: "medium", label: "Medium", activeColor: "hsl(var(--brand-teal) / 0.7)" },
    { key: "long", label: "Long", activeColor: "hsl(var(--brand-teal))" },
  ];

  // Rate strength is a separate market-quality signal based on globalAverage
  const rateStrength = globalAverage <= 1.99 ? 0 : globalAverage <= 3.50 ? 1 : 2;
  const strengthLabels = ["Weak", "Average", "Strong"];
  const strengthColors = ["text-destructive", "text-muted-foreground", "text-success"];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1 rounded-md border bg-muted p-0.5">
        {(["Van", "Reefer", "Flatbed"] as Equipment[]).map(eq => (
          <button
            key={eq}
            onClick={() => onEquipmentChange(eq)}
            className={cn(
              "flex-1 rounded py-1.5 text-caption font-medium transition-all duration-150",
              equipment === eq
                ? "bg-card text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {eq}
          </button>
        ))}
      </div>

      {/* Lane strength visualization */}
      <div className="relative rounded-md overflow-hidden border bg-card" style={{ minHeight: "180px" }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-[180px] text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-[10px]">Loading lane data…</span>
          </div>
        ) : lanes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[180px] text-muted-foreground gap-1.5">
            <span className="text-[11px] font-medium">No {equipment} lane data</span>
            <span className="text-[10px]">Post loads to generate lane insights</span>
          </div>
        ) : (
          <div className="p-2.5 space-y-1.5">
            {/* Global average + rate strength */}
            <div className="flex items-center justify-between px-2 py-1 rounded bg-muted/40">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                {equipment} Avg RPM
              </span>
              <div className="flex items-center gap-2">
                <span className={cn("text-[10px] font-semibold", strengthColors[rateStrength])}>
                  {strengthLabels[rateStrength]}
                </span>
                <span className="text-[12px] font-bold text-foreground">
                  ${globalAverage.toFixed(2)}/mi
                </span>
              </div>
            </div>

            {/* Top lanes with strength bars */}
            {lanes.map((lane, i) => {
              const maxRate = lanes[0]?.rate || 1;
              const barWidth = Math.max(15, Math.round((lane.rate / maxRate) * 100));
              const isTop = i === 0;

              return (
                <div
                  key={i}
                  onClick={() => handleLaneClick(lane)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/30 transition-colors cursor-pointer"
                  title={`Search loads: ${lane.name}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      {isTop ? (
                        <TrendingUp className="h-2.5 w-2.5 text-success flex-shrink-0" />
                      ) : i === lanes.length - 1 ? (
                        <TrendingDown className="h-2.5 w-2.5 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <Minus className="h-2.5 w-2.5 text-muted-foreground flex-shrink-0" />
                      )}
                      <span className="text-[10px] font-semibold text-foreground truncate">
                        {lane.shortName}
                      </span>
                    </div>
                    {/* Strength bar */}
                    <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: isTop
                            ? "hsl(var(--brand-teal))"
                            : i < 2
                              ? "hsl(var(--brand-teal) / 0.6)"
                              : "hsl(var(--muted-foreground) / 0.35)",
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0">
                    <span className={cn(
                      "text-[10px] font-bold",
                      isTop ? "text-foreground" : "text-muted-foreground"
                    )}>
                      {lane.rateDisplay}/mi
                    </span>
                    <span className="text-[9px] text-muted-foreground">
                      {lane.volume} load{lane.volume !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Haul Type filter (clickable distance filter) */}
      <div className="flex items-center gap-2 border-t bg-muted/50 px-3 py-2 -mx-3">
        <span className="text-[9px] text-muted-foreground whitespace-nowrap">Haul:</span>
        <div className="flex items-center gap-2">
          {haulTypes.map((ht) => (
            <button
              key={ht.key}
              onClick={() => onHaulTypeChange(haulType === ht.key ? null : ht.key!)}
              className="flex items-center gap-1 group"
            >
              <div
                className="h-2 w-5 rounded-sm transition-colors"
                style={{
                  backgroundColor: haulType === ht.key
                    ? ht.activeColor
                    : "hsl(var(--muted-foreground) / 0.25)",
                }}
              />
              <span className={cn(
                "text-[9px] whitespace-nowrap transition-colors",
                haulType === ht.key ? "text-foreground font-medium" : "text-muted-foreground group-hover:text-foreground"
              )}>
                {ht.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
